import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Popover,
  Box,
  Typography,
  Button,
  Stack,
  Badge
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enUS, de } from 'date-fns/locale';
import {
  CalendarToday as CalendarIcon,
  Clear as ClearIcon
} from '@mui/icons-material';

export interface DateRangeFilters {
  createdFrom: Date | null;
  createdTo: Date | null;
  updatedFrom: Date | null;
  updatedTo: Date | null;
}

export interface DateRangeFilterPopoverProps {
  dateFilters: DateRangeFilters;
  onDateFiltersChange: (filters: DateRangeFilters) => void;
}

/**
 * DateRangeFilterPopover - Collapsible date range filter component
 *
 * Provides a button that opens a popover with date range pickers for:
 * - Created Date (from/to)
 * - Updated Date (from/to)
 *
 * Shows a badge indicator when any date filter is active.
 */
export const DateRangeFilterPopover: React.FC<DateRangeFilterPopoverProps> = ({
  dateFilters,
  onDateFiltersChange
}) => {
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  // Get the appropriate date-fns locale based on current i18n language
  const dateLocale = i18n.language === 'de' ? de : enUS;

  const handleOpenPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // Check if any date filter is active
  const hasActiveDateFilters = Boolean(
    dateFilters.createdFrom ||
    dateFilters.createdTo ||
    dateFilters.updatedFrom ||
    dateFilters.updatedTo
  );

  // Count active filters for badge
  const activeFilterCount = [
    dateFilters.createdFrom,
    dateFilters.createdTo,
    dateFilters.updatedFrom,
    dateFilters.updatedTo
  ].filter(Boolean).length;

  const handleClearDates = () => {
    onDateFiltersChange({
      createdFrom: null,
      createdTo: null,
      updatedFrom: null,
      updatedTo: null
    });
  };

  const handleDateChange = (field: keyof DateRangeFilters, value: Date | null) => {
    onDateFiltersChange({
      ...dateFilters,
      [field]: value
    });
  };

  return (
    <>
      <Badge
        badgeContent={activeFilterCount}
        color="primary"
        invisible={!hasActiveDateFilters}
      >
        <Button
          variant={hasActiveDateFilters ? 'contained' : 'outlined'}
          size="small"
          startIcon={<CalendarIcon />}
          onClick={handleOpenPopover}
        >
          {t('documents.filters.dateFilters')}
        </Button>
      </Badge>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 3, minWidth: 400 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
            <Stack spacing={3}>
              {/* Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  {t('documents.filters.dateFilters')}
                </Typography>
                {hasActiveDateFilters && (
                  <Button
                    size="small"
                    startIcon={<ClearIcon />}
                    onClick={handleClearDates}
                  >
                    {t('documents.filters.clearDates')}
                  </Button>
                )}
              </Box>

              {/* Created Date Range */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t('documents.filters.createdDateRange')}
                </Typography>
                <Stack spacing={2}>
                  <DatePicker
                    label={t('documents.filters.createdFrom')}
                    value={dateFilters.createdFrom}
                    onChange={(value) => handleDateChange('createdFrom', value)}
                    slotProps={{
                      textField: { size: 'small', fullWidth: true }
                    }}
                  />
                  <DatePicker
                    label={t('documents.filters.createdTo')}
                    value={dateFilters.createdTo}
                    onChange={(value) => handleDateChange('createdTo', value)}
                    minDate={dateFilters.createdFrom || undefined}
                    slotProps={{
                      textField: { size: 'small', fullWidth: true }
                    }}
                  />
                </Stack>
              </Box>

              {/* Updated Date Range */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t('documents.filters.updatedDateRange')}
                </Typography>
                <Stack spacing={2}>
                  <DatePicker
                    label={t('documents.filters.updatedFrom')}
                    value={dateFilters.updatedFrom}
                    onChange={(value) => handleDateChange('updatedFrom', value)}
                    slotProps={{
                      textField: { size: 'small', fullWidth: true }
                    }}
                  />
                  <DatePicker
                    label={t('documents.filters.updatedTo')}
                    value={dateFilters.updatedTo}
                    onChange={(value) => handleDateChange('updatedTo', value)}
                    minDate={dateFilters.updatedFrom || undefined}
                    slotProps={{
                      textField: { size: 'small', fullWidth: true }
                    }}
                  />
                </Stack>
              </Box>

              {/* Apply Button */}
              <Button
                variant="contained"
                fullWidth
                onClick={handleClosePopover}
              >
                {t('documents.filters.applyDateFilters')}
              </Button>
            </Stack>
          </LocalizationProvider>
        </Box>
      </Popover>
    </>
  );
};
